import type { MouseEvent, ReactNode } from "react";
import { forwardRef } from "react";

import type {
  ButtonVariants,
  PopoverProps,
} from "@braintrust/braintrust-ui-components";
import {
  Button,
  Popover,
  Typography,
} from "@braintrust/braintrust-ui-components";

export type FilterPopoverProps = {
  label: string;
  icon: ReactNode;
  isFilterApplied: boolean;
  isFilterOpen: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  onPopoverEnter?: () => void;
  anchorElement: HTMLElement | null;
  children: ReactNode;
  variant?: PopoverAnchorComponentProps["variant"];
  anchorClassName?: string;
} & Pick<PopoverProps, "anchorOrigin" | "transformOrigin">;

export const FilterPopover = ({
  label,
  icon,
  isFilterApplied,
  children,
  isFilterOpen,
  onClick,
  onClose,
  onPopoverEnter,
  anchorElement,
  variant,
  anchorClassName,
  ...rest
}: FilterPopoverProps) => {
  return (
    <Popover
      open={isFilterOpen}
      onClose={onClose}
      sx={{ mt: 1 }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      disableRestoreFocus
      anchorEl={anchorElement}
      anchor={
        <PopoverAnchorComponent
          label={label}
          icon={icon}
          isActive={isFilterApplied}
          isOpen={isFilterOpen}
          onClick={onClick}
          variant={variant}
          className={anchorClassName}
        />
      }
      {...rest}
      slotProps={{ transition: { onEntered: onPopoverEnter } }}
    >
      {children}
    </Popover>
  );
};

type PopoverAnchorComponentProps = {
  label: string;
  icon: ReactNode;
  isActive: boolean;
  isOpen: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  variant?: "green" | "violet";
  className?: string;
};

const PopoverAnchorComponent = forwardRef<
  HTMLButtonElement,
  PopoverAnchorComponentProps
>(
  (
    { label, icon, isActive, onClick, variant = "violet", isOpen, className },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        onClick={onClick}
        variant={getVariant({ isActive, isOpen, variant })}
        startIcon={icon}
        shape="squared"
        sx={{
          "&& .icon > svg": {
            fontSize: "18px",
          },
          "&& .icon--start": {
            mr: "5px",
          },
        }}
        className={className}
      >
        <Typography component="p" size="small">
          {label}
        </Typography>
      </Button>
    );
  },
);

const getVariant = ({
  isOpen,
  variant,
  isActive,
}: {
  isOpen: boolean;
  isActive: boolean;
  variant: "green" | "violet";
}): ButtonVariants => {
  if (isOpen) {
    return `${variant}-with-dark-border`;
  }

  if (isActive) {
    return `medium-${variant}`;
  }

  return `white-${variant}-dark-border`;
};
